import { useTheme } from "@mui/material/styles";

// Common Styles
import {
  Paragraph,
  SectionHeader,
  SectionTitle,
  ShowLabel,
  WorkspacePanel,
  WorkspaceSection,
  WorkSpaceTitleRow,
} from "../../styles/commonStyles";

// Icons
import { PackageIcon } from "../Svg";

// Colors
import { colors } from "../../styles/colors";

const getDishName = (item: any) => {
  return (
    item?.dishName ||
    item?.dish?.name ||
    item?.DishModel?.name ||
    item?.Dish?.name ||
    (item?.dishId ? `Dish #${item?.dishId}` : "")
  );
};

const getEquipmentName = (equipment: any) => {
  return (
    equipment?.EquipmentModelV2?.name ||
    equipment?.equipment?.name ||
    equipment?.name ||
    ""
  );
};

const getInstructionCategoryName = (item: any) => {
  return (
    item?.categoryName ||
    item?.InstructionCategoryModel?.name ||
    item?.DishInstructionCategory?.name ||
    item?.category?.name ||
    (item?.categoryId ? `Category #${item?.categoryId}` : "")
  );
};

type NormalizedEquipment = {
  name: string;
  dishNames: string[];
  equipmentId?: number | string;
};

type NormalizedInstruction = {
  id: string;
  dishName: string;
  instruction: string;
};

type NormalizedInstructionGroup = {
  categoryId?: number | string;
  categoryName: string;
  instructions: NormalizedInstruction[];
};

const normalizeRequiredEquipments = (record: any) => {
  const topLevelEquipments = Array.isArray(record?.requiredEquipments)
    ? record.requiredEquipments
    : [];

  const nestedEquipments =
    topLevelEquipments.length > 0
      ? []
      : (record?.dishes || []).filter((dish: any) => !dish?.deletedAt).reduce((acc: any[], dish: any) => {
          const dishName =
            getDishName(dish) || getDishName(dish?.dish) || dish?.name || "";
          const equipments = [
            ...(Array.isArray(dish?.requiredEquipments)
              ? dish.requiredEquipments
              : []),
            ...(Array.isArray(dish?.equipments) ? dish.equipments : []),
            ...(Array.isArray(dish?.dish?.requiredEquipments)
              ? dish.dish.requiredEquipments
              : []),
            ...(Array.isArray(dish?.dish?.equipments) ? dish.dish.equipments : []),
          ];

          equipments.forEach((equipment: any) => {
            acc.push({
              ...equipment,
              dishName: equipment?.dishName || dishName,
            });
          });

          return acc;
        }, []);

  const equipmentMap = new Map<string, NormalizedEquipment>();

  [...topLevelEquipments, ...nestedEquipments].forEach((equipment: any) => {
    const name = getEquipmentName(equipment);
    if (!name) return;

    const key = String(
      equipment?.equipmentId || equipment?.EquipmentModelV2?.id || name,
    );
    const dishName = getDishName(equipment);
    const existing: NormalizedEquipment = equipmentMap.get(key) || {
      name,
      dishNames: [],
      equipmentId: equipment?.equipmentId || equipment?.EquipmentModelV2?.id,
    };

    if (dishName && !existing.dishNames.includes(dishName)) {
      existing.dishNames.push(dishName);
    }

    equipmentMap.set(key, existing);
  });

  return Array.from(equipmentMap.values()).sort((first, second) =>
    first.name.localeCompare(second.name),
  );
};

const normalizeInstructionGroups = (record: any) => {
  const topLevelGroups = Array.isArray(record?.dishInstructions)
    ? record.dishInstructions
    : [];

  const instructionMap = new Map<string, NormalizedInstructionGroup>();

  const addInstruction = (instruction: any, fallbackCategory?: any, fallbackDish?: any) => {
    const instructionText = instruction?.instruction?.trim?.() || "";
    if (!instructionText) return;

    const categoryId =
      instruction?.categoryId ||
      instruction?.category?.id ||
      fallbackCategory?.categoryId ||
      fallbackCategory?.id;
    const categoryName =
      fallbackCategory?.categoryName ||
      getInstructionCategoryName(fallbackCategory) ||
      getInstructionCategoryName(instruction) ||
      "Instruction";
    const dishName =
      getDishName(instruction) || getDishName(fallbackDish) || "Selected dish";
    const groupKey = String(categoryId || categoryName);
    const existing: NormalizedInstructionGroup = instructionMap.get(groupKey) || {
      categoryId,
      categoryName,
      instructions: [],
    };
    const itemKey = String(
      instruction?.id || `${dishName}-${instructionText}-${existing.instructions.length}`,
    );

    const hasDuplicate = existing.instructions.some(
      (item) =>
        item.dishName === dishName && item.instruction === instructionText,
    );

    if (!hasDuplicate) {
      existing.instructions.push({
        id: itemKey,
        dishName,
        instruction: instructionText,
      });
    }

    instructionMap.set(groupKey, existing);
  };

  if (topLevelGroups.length > 0) {
    topLevelGroups.forEach((group: any) => {
      (group?.instructions || []).forEach((instruction: any) => {
        addInstruction(instruction, group);
      });
    });
  } else {
    (record?.dishes || []).filter((dish: any) => !dish?.deletedAt).forEach((dish: any) => {
      const flatInstructions = [
        ...(Array.isArray(dish?.instructions) ? dish.instructions : []),
        ...(Array.isArray(dish?.dish?.instructions) ? dish.dish.instructions : []),
      ];
      const groupedInstructions = [
        ...(Array.isArray(dish?.dishInstructions) ? dish.dishInstructions : []),
        ...(Array.isArray(dish?.dish?.dishInstructions)
          ? dish.dish.dishInstructions
          : []),
      ];

      flatInstructions.forEach((instruction: any) => addInstruction(instruction, null, dish));
      groupedInstructions.forEach((group: any) => {
        (group?.instructions || []).forEach((instruction: any) => {
          addInstruction(instruction, group, dish);
        });
      });
    });
  }

  return Array.from(instructionMap.values())
    .map((group) => ({
      ...group,
      instructions: group.instructions.sort((first, second) =>
        first.dishName.localeCompare(second.dishName),
      ),
    }))
    .sort((first, second) =>
      first.categoryName.localeCompare(second.categoryName),
    );
};

const ChefForPartyEquipmentInstructions = ({ record }: { record: any }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const equipments = normalizeRequiredEquipments(record);
  const instructionGroups = normalizeInstructionGroups(record);

  const emptyStateStyle = {
    color: isDarkMode ? colors.dark.textGrey : colors.light.textGrey,
    fontStyle: "italic",
  } as const;

  return (
    <WorkspacePanel>
      <WorkspaceSection>
        <SectionHeader>
          <WorkSpaceTitleRow>
            <PackageIcon color={colors.primary} height={20} width={20} />
            <SectionTitle>Equipment & Instructions</SectionTitle>
          </WorkSpaceTitleRow>
        </SectionHeader>
      </WorkspaceSection>

      <div style={{ marginBottom: "16px" }}>
        <ShowLabel>Required Equipments</ShowLabel>
        {equipments.length > 0 ? (
          equipments.map((equipment) => (
            <Paragraph
              key={equipment.equipmentId || equipment.name}
              style={{ marginBottom: "8px" }}
            >
              {equipment.name}
            </Paragraph>
          ))
        ) : (
          <Paragraph style={emptyStateStyle}>-</Paragraph>
        )}
      </div>

      <div style={{ borderTop: "1px solid #e2e8f0", paddingTop: "16px" }}>
        <ShowLabel>Dish Instructions</ShowLabel>
        {instructionGroups.length > 0 ? (
          instructionGroups.map((group) => (
            <div
              key={group.categoryId || group.categoryName}
              style={{ marginBottom: "14px" }}
            >
              <ShowLabel style={{ marginBottom: "4px" }}>
                {group.categoryName}
              </ShowLabel>
              {group.instructions.map((instruction) => (
                <Paragraph
                  key={instruction.id}
                  style={{ marginBottom: "6px", lineHeight: 1.5 }}
                >
                  {instruction.dishName ? `${instruction.dishName}: ` : ""}
                  {instruction.instruction}
                </Paragraph>
              ))}
            </div>
          ))
        ) : (
          <Paragraph style={emptyStateStyle}>-</Paragraph>
        )}
      </div>
    </WorkspacePanel>
  );
};

export default ChefForPartyEquipmentInstructions;
